<template>
  <div class="category-manager">
    <!-- 1. 搜索区域 (保持原样) -->
    <el-card class="search-box" shadow="never">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="分类名称">
          <el-input v-model="searchForm.catName" placeholder="请输入" clearable />
        </el-form-item>
        <el-form-item label="分类状态">
          <el-select v-model="searchForm.catStatus" placeholder="请选择" clearable style="width: 140px">
            <el-option label="上架" value="grounding" />
            <el-option label="下架" value="off_shelf" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 2. 操作栏 -->
    <div class="toolbar">
      <el-button type="primary" @click="handleAdd">新增分类</el-button>
    </div>

    <!-- 3. 主表格 (分类级) -->
    <!-- 添加 id="mainTable" 用于定位 DOM -->
    <el-table
      id="mainTable"
      v-loading="loading"
      :data="tableData"
      row-key="id"
      border
      :row-class-name="getRowClassName"
      class="main-table"
    >
      <!-- 【子表格入口】展开行列 -->
      <el-table-column type="expand">
        <template #default="props">
          <div class="sub-table-container">
            <h4 class="sub-title">下属商品列表 (拖动图标排序)</h4>
            <!-- 嵌套的子表格 -->
            <!-- 使用 :ref 动态绑定，当展开渲染时初始化子表格拖拽 -->
            <el-table
              :ref="(el) => setSubTableRef(el, props.row)"
              :data="props.row.spuInfoVOList"
              row-key="id"
              border
              size="small"
              stripe
            >
              <!-- 子表格拖拽句柄 -->
              <el-table-column width="50" align="center">
                <template #default>
                  <el-icon class="sub-drag-handle" style="cursor: grab; font-size: 16px;"><Rank /></el-icon>
                </template>
              </el-table-column>

              <el-table-column label="商品名称" prop="spuName" />
              <el-table-column label="商品ID" prop="id" width="120" />
              <el-table-column label="商品销量" prop="price" width="120">
                <template #default="{row}">{{ row.price || 0 }}</template>
              </el-table-column>
              <el-table-column label="上架时间" prop="gmtCreate" :formatter="formatTime" width="180" />
              <el-table-column label="操作" width="100">
                <template  #default="{row}">
                  <el-button link type="primary" @click="handleAction('removeCat',row)">移除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>

      <!-- 主表格拖拽句柄 -->
      <el-table-column label="排序" width="60" align="center">
        <template #default>
          <el-icon class="main-drag-handle" style="cursor: grab; font-size: 18px; color: #909399;"><Rank /></el-icon>
        </template>
      </el-table-column>

      <!-- 分类级列 -->
      <el-table-column label="分类名称" prop="catName" />
      <el-table-column label="分类ID" prop="id" width="120" />
      <el-table-column label="商品数量" prop="shopGoodsCount" width="100" align="center" />
      <el-table-column label="创建时间" prop="gmtCreate" :formatter="formatTime" width="180" />
      <el-table-column label="分类状态" prop="catStatus" width="100">
        <template #default="{ row }">
          <el-tag :type="row.catStatus === 'grounding' ? 'success' : 'info'">
            {{ row.catStatus === 'grounding' ? '上架' : '下架' }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 分类级操作 (严格保持原样) -->
      <el-table-column label="操作" fixed="right" width="220">
        <template #default="{ row }">
          <el-button link type="primary" @click="handleAction('edit', row)">编辑</el-button>
          <el-button link type="primary" @click="handleAction('hide', row)">隐藏</el-button>
          <!-- 这里的按钮逻辑完全保留 -->
          <el-button link type="warning"
                     v-if="row.catStatus === 'grounding'"
                     @click="handleAction('off', row)">下架</el-button>
          <el-button
            v-else
            link type="success" @click="handleAction('on', row)">上架</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 4. 分页 -->
    <div class="pagination">
      <el-pagination
        layout="total, sizes, prev, pager, next"
        :total="total"
        v-model:page-size="pageSize"
        v-model:current-page="currentPage"
        @current-change="fetchData"
      />
    </div>
    <CategoryDialog
      ref="categoryDialogRef"
      :api-submit="apiService"
      @refresh="fetchList"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick } from 'vue'
// 引入 Sortable 和 图标
import Sortable from 'sortablejs'
import { Rank } from '@element-plus/icons-vue'

import CategoryDialog from './CategoryDialog.vue'
import { ElMessage } from 'element-plus'
import { dateUtil, useCurrentAppInstance } from '@zto/zpage'

const app = useCurrentAppInstance()
const { Message, MessageBox } = app.useMessage()

// --- 状态定义 ---
const loading = ref(false)
const tableData = ref<any[]>([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)
const categoryDialogRef = ref()

// 存储子表格的 DOM 引用，防止重复初始化 Sortable
const subSortableSet = new WeakSet()

const formatTime = (_row: any, _column: any, cellValue: string) => {
  if (!cellValue) return '-'
  return dateUtil.parseAndFormat(cellValue)
}

const searchForm = reactive({
  catName: '',
  catStatus: ''
})

// --- 数据获取 ---
const fetchData = async () => {
  loading.value = true
  try {
    const res = await app.request({
      api: 'pc/catInfo/list',
      params: {
        pageSize: 100, // 排序通常需要较多数据
        pageIndex: 1,
        catName: searchForm.catName,
      }
    })
    tableData.value = res.data.map((it: any) => {
      return {
        ...it,
        shopGoodsCount: it.spuInfoVOList ? it.spuInfoVOList.length : 0,
      }
    })
    total.value = tableData.value.length

    // 数据更新后，初始化主表格拖拽
    nextTick(() => {
      initMainSort()
    })
  } finally {
    loading.value = false
  }
}

const fetchList = () => {
  fetchData()
}

// 提交 API 的封装
const apiService = async (payload: any) => {
  const url = payload.id ? 'pc/catInfo/update' : 'pc/catInfo/create'
  await app.request({
    api: url,
    params: { ...payload }
  })
  return Promise.resolve()
}

// --- 核心：拖拽排序逻辑 ---

// 1. 初始化主表格（分类）排序
const initMainSort = () => {
  const el = document.querySelector('#mainTable .el-table__body-wrapper tbody')
  if (!el) return

  Sortable.create(el as HTMLElement, {
    handle: '.main-drag-handle', // 只能拖拽图标
    animation: 150,
    // 关键：指定只拖拽数据行，忽略 Element Plus 的展开详情行
    draggable: '.el-table__row',
    onEnd: ({ oldIndex, newIndex }: any) => {
      if (oldIndex === newIndex) return

      // 更新本地数据，Vue 会自动重新渲染 DOM 顺序
      const currRow = tableData.value.splice(oldIndex, 1)[0]
      tableData.value.splice(newIndex, 0, currRow)

      // 触发业务回调
      handleDragEnd('CATEGORY', {
        movedItem: currRow,
        newIndex,
        oldIndex,
        list: tableData.value
      })
    }
  })
}

// 2. 初始化子表格（商品）排序
// 通过 :ref 回调动态触发
const setSubTableRef = (el: any, parentRow: any) => {
  if (el && el.$el && !subSortableSet.has(el.$el)) {
    subSortableSet.add(el.$el)
    const tbody = el.$el.querySelector('.el-table__body-wrapper tbody')

    if (tbody) {
      Sortable.create(tbody, {
        handle: '.sub-drag-handle',
        animation: 150,
        group: 'spu-items', // 允许跨同类子表格拖拽（可选，不想要可去掉）
        onEnd: ({ oldIndex, newIndex }: any) => {
          if (oldIndex === newIndex) return

          const list = parentRow.spuInfoVOList
          const currItem = list.splice(oldIndex, 1)[0]
          list.splice(newIndex, 0, currItem)

          handleDragEnd('PRODUCT', {
            parentCatId: parentRow.id,
            movedItem: currItem,
            list: list
          })
        }
      })
    }
  }
}

// 3. 拖拽结束统一回调
const handleDragEnd = (type: string, payload: any) => {
  console.log(`[${type} 排序完成]`, payload)

  // TODO: 在这里调用后端 API 保存顺序
  // const ids = payload.list.map((item: any) => item.id)
  // await app.request({ api: '/saveSort', data: { ids, type } })

  ElMessage.success(`${type === 'CATEGORY' ? '分类' : '商品'}排序已更新`)
}


// --- 事件处理 (保持原样) ---
const handleSearch = () => {
  currentPage.value = 1
  fetchData()
}

const handleReset = () => {
  searchForm.catName = ''
  searchForm.catStatus = ''
  handleSearch()
}

const handleAdd = () => {
  categoryDialogRef.value.open('ADD')
}

// 操作栏逻辑 (上下架/隐藏/编辑/移除)
const handleAction = (action: string, row: any) => {
  let actName = '上架'
  if (action ==='off'){
    actName = '下架'
  }
  else if (action === 'hide'){
    actName = '隐藏'
  }
  else if (action === 'edit'){
    categoryDialogRef.value.open('EDIT',row)
    return
  }
  else if (action === 'removeCat'){
    actName = '移除'
  }
  MessageBox.confirm(`确定要执行操作: ${actName}`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
    callback: async (act: string) => {
      if (act === 'cancel'){
        return
      }
      if (action ==='off'){
        await app.request({
          api: 'pc/catInfo/offShelf',
          method: 'POST',
          data: {
            catCode: row.catCode,
            catStatus: action,
          }
        })
      }
      else if (action === 'removeCat'){
        await app.request({
          api: 'pc/goods/unbindCat',
          method: 'POST',
          data: {
            spuCode: row.spuCode,
            catStatus: action,
          }
        })
      }
      else if (action === 'hide'){
        await app.request({
          api: 'pc/catInfo/hide',
          method: 'POST',
          data: {
            catCode: row.catCode,
            catStatus: action,
          }
        })
      }
      else {
        // 上架逻辑
        await app.request({
          api: 'pc/catInfo/grounding',
          method: 'POST',
          data: {
            catCode: row.catCode,
            catStatus: action,
          }
        })
      }
      ElMessage.success(`操作成功`)
      fetchData()
    },
  })
}

// 动态判断行类名
const getRowClassName = ({ row }: { row: any }) => {
  if (!row.spuInfoVOList || row.spuInfoVOList.length === 0) {
    return 'hide-expand'
  }
  return ''
}

onMounted(fetchData)
</script>

<style scoped>
.category-manager {
  padding: 20px;
  background-color: #f5f7fa;
}

.search-box { margin-bottom: 20px; }
.toolbar { margin-bottom: 10px; }

.sub-table-container {
  padding: 10px 40px 20px 50px;
  background-color: #fafafa;
}

.sub-title {
  margin-bottom: 10px;
  font-size: 13px;
  color: #909399;
}

/* 隐藏无数据行的展开箭头 */
:deep(.hide-expand .el-table__expand-column .el-table__expand-icon) {
  display: none !important;
}
:deep(.hide-expand .el-table__expand-column) {
  pointer-events: none;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 拖拽相关样式 */
.main-drag-handle:hover, .sub-drag-handle:hover {
  color: #409EFF !important;
  cursor: grab;
}
.main-drag-handle:active, .sub-drag-handle:active {
  cursor: grabbing;
}
</style>
